import { useState } from "react";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { FadeIn, StaggerContainer, StaggerItem } from "@/components/FadeIn";
import { SectionHeading, GoldDivider, GoldDividerLeft } from "@/components/SectionHeading";
import { Toaster } from "@/components/ui/sonner";

const contactInfo = [
    {
        icon: MapPin,
        title: "Adresse",
        lines: ["Neuhauser Str. 27", "80331 München"],
    },
    {
        icon: Phone,
        title: "Telefon",
        lines: ["+49 89 401 929 200", "+49 89 131 007"],
    },
    {
        icon: Mail,
        title: "E-Mail",
        lines: ["info@steuerkanzlei-ruberto.de", "info@steuerkanzlei-ruberto.de"],
    },
    {
        icon: Clock,
        title: "Öffnungszeiten",
        lines: ["Mo – Fr: 9:00 – 18:00 Uhr", "Nach Vereinbarung"],
    },
];

export default function Contact() {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Simulate form submission (frontend-only mockup)
        await new Promise((resolve) => setTimeout(resolve, 1500));

        toast.success("Nachricht gesendet", {
            description: "Vielen Dank für Ihre Nachricht. Wir werden uns zeitnah bei Ihnen melden.",
        });

        setFormData({
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: "",
        });
        setIsSubmitting(false);
    };

    return (
        <div className="bg-background">
            <Toaster position="top-right" />
            
            {/* Hero Section */}
            <section className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80&auto=format&fit=crop"
                        alt="Kontakt"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/70 to-background/40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-background/30 to-background/60" />
                </div>
                
                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl">
                        <FadeIn>
                            <span className="font-sans text-xs tracking-wide-premium uppercase text-gold mb-6 block">
                                Kontakt
                            </span>
                        </FadeIn>

                        <FadeIn delay={0.1}>
                            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground leading-tight mb-8">
                                Nehmen Sie Kontakt mit uns auf
                            </h1>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <GoldDividerLeft className="mb-8" />
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <p className="font-sans text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
                                Wir freuen uns auf Ihre Nachricht. Vereinbaren Sie ein 
                                persönliches Beratungsgespräch oder stellen Sie uns 
                                Ihre Fragen direkt.
                            </p>
                        </FadeIn>
                    </div>
                </div>
            </section>

            {/* Contact Info Cards */}
            <section className="py-16 lg:py-20 bg-charcoal-mid">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <StaggerContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={0.1}>
                        {contactInfo.map((item) => (
                            <StaggerItem key={item.title}>
                                <Card className="bg-card border-border p-6 h-full hover:border-gold/30 transition-colors duration-400">
                                    <item.icon className="h-5 w-5 text-gold mb-4" strokeWidth={1.5} />
                                    <h3 className="font-sans text-xs tracking-premium uppercase text-foreground mb-3">
                                        {item.title}
                                    </h3>
                                    <div className="space-y-1">
                                        {item.lines.map((line, index) => (
                                            <p key={index} className="font-sans text-sm text-muted-foreground">
                                                {line}
                                            </p>
                                        ))}
                                    </div>
                                </Card>
                            </StaggerItem>
                        ))}
                    </StaggerContainer>
                </div>
            </section>

            {/* Contact Form & Map */}
            <section className="py-24 lg:py-32 bg-background">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
                        {/* Form */}
                        <div>
                            <FadeIn>
                                <SectionHeading
                                    eyebrow="Kontaktformular"
                                    title="Schreiben Sie uns"
                                    description="Füllen Sie das Formular aus und wir melden uns zeitnah bei Ihnen."
                                    className="mb-10"
                                />
                            </FadeIn>

                            <FadeIn delay={0.2}>
                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="grid sm:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="name" className="font-sans text-xs tracking-premium uppercase text-muted-foreground">
                                                Name *
                                            </Label>
                                            <Input
                                                id="name"
                                                name="name"
                                                type="text"
                                                required
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:border-gold focus:ring-gold/20 h-12"
                                                placeholder="Ihr Name"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="email" className="font-sans text-xs tracking-premium uppercase text-muted-foreground">
                                                E-Mail *
                                            </Label>
                                            <Input
                                                id="email"
                                                name="email"
                                                type="email"
                                                required
                                                value={formData.email}
                                                onChange={handleChange}
                                                className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:border-gold focus:ring-gold/20 h-12"
                                                placeholder="ihre@email.de"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid sm:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="phone" className="font-sans text-xs tracking-premium uppercase text-muted-foreground">
                                                Telefon
                                            </Label>
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:border-gold focus:ring-gold/20 h-12"
                                                placeholder="+49 ..."
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="subject" className="font-sans text-xs tracking-premium uppercase text-muted-foreground">
                                                Betreff *
                                            </Label>
                                            <Input
                                                id="subject"
                                                name="subject"
                                                type="text"
                                                required
                                                value={formData.subject}
                                                onChange={handleChange}
                                                className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:border-gold focus:ring-gold/20 h-12"
                                                placeholder="Ihr Anliegen"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="message" className="font-sans text-xs tracking-premium uppercase text-muted-foreground">
                                            Nachricht *
                                        </Label>
                                        <Textarea
                                            id="message"
                                            name="message"
                                            required
                                            rows={6}
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="bg-card border-border text-foreground placeholder:text-muted-foreground focus:border-gold focus:ring-gold/20 resize-none"
                                            placeholder="Wie können wir Ihnen helfen?"
                                        />
                                    </div>

                                    <div className="pt-4">
                                        <Button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="bg-gold text-primary-foreground hover:bg-gold-muted transition-all duration-400 px-8 py-6 font-sans text-sm tracking-premium uppercase disabled:opacity-50"
                                        >
                                            {isSubmitting ? (
                                                "Wird gesendet..."
                                            ) : (
                                                <>
                                                    Nachricht senden
                                                    <Send className="ml-2 h-4 w-4" />
                                                </>
                                            )}
                                        </Button>
                                    </div>

                                    <p className="font-sans text-xs text-muted-foreground mt-4">
                                        * Pflichtfelder. Mit dem Absenden stimmen Sie der Verarbeitung 
                                        Ihrer Daten gemäß unserer Datenschutzerklärung zu.
                                    </p>
                                </form>
                            </FadeIn>
                        </div>

                        {/* Map/Location */}
                        <div>
                            <FadeIn delay={0.3}>
                                <Card className="bg-card border-border overflow-hidden h-full min-h-[500px]">
                                    <div className="relative h-full flex flex-col">
                                        {/* Google Maps Embed */}
                                        <div className="flex-1 min-h-[400px]">
                                            <iframe
                                                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2661.5!2d11.5715!3d48.1655!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x479e75f35faed5f3%3A0x1017b3d4b0291e0!2sNeuhauser%20Str.%2027%2C%2080331%20M%C3%BCnchen!5e0!3m2!1sde!2sde!4v1700000000000"
                                                width="100%"
                                                height="100%"
                                                style={{ border: 0, minHeight: 400 }}
                                                allowFullScreen
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                                title="Standort Steuerberatung Ruberto – Neuhauser Str. 27, München"
                                                className="w-full h-full"
                                            />
                                        </div>
                                        {/* Location info bar */}
                                        <div className="p-6 bg-card border-t border-border">
                                            <div className="flex items-center gap-3">
                                                <MapPin className="h-5 w-5 text-gold flex-shrink-0" strokeWidth={1.5} />
                                                <div>
                                                    <p className="font-sans text-sm text-foreground">
                                                        Neuhauser Str. 27, 80331 München
                                                    </p>
                                                    <p className="font-sans text-xs text-muted-foreground mt-1">
                                                        Im Herzen der Münchner Innenstadt – gut erreichbar mit öffentlichen Verkehrsmitteln
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </FadeIn>
                        </div>
                    </div>
                </div>
            </section>

            {/* Direct Contact */}
            <section className="py-24 lg:py-32 bg-charcoal-mid border-t border-border">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto text-center">
                        <FadeIn>
                            <GoldDivider className="mb-10" />
                        </FadeIn>
                        
                        <FadeIn delay={0.1}>
                            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                                Direkter Kontakt
                            </h2>
                        </FadeIn>

                        <FadeIn delay={0.2}>
                            <p className="font-sans text-base text-muted-foreground mb-10">
                                Sie erreichen uns auch telefonisch oder per E-Mail.
                            </p>
                        </FadeIn>

                        <FadeIn delay={0.3}>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-8">
                                <a
                                    href="tel:+498989526284"
                                    className="flex items-center gap-3 text-foreground hover:text-gold transition-colors duration-300"
                                >
                                    <Phone className="h-5 w-5 text-gold" strokeWidth={1.5} />
                                    <span className="font-sans text-lg">+49 89 526 284</span>
                                </a>
                                
                                <Separator orientation="vertical" className="hidden sm:block h-6 bg-border" />
                                
                                <a
                                    href="mailto:info@steuerkanzlei-ruberto.de"
                                    className="flex items-center gap-3 text-foreground hover:text-gold transition-colors duration-300"
                                >
                                    <Mail className="h-5 w-5 text-gold" strokeWidth={1.5} />
                                    <span className="font-sans text-lg">info@steuerkanzlei-ruberto.de</span>
                                </a>
                            </div>
                        </FadeIn>
                    </div>
                </div>
            </section>
        </div>
    );
}
